import { AitLocation, AitTableData, AitTablePosition } from "aic";
import { AnnotationWhereGroup } from "components/annotations/AnnotationWhereGroup";
/**
 * Update table data with an annotation at the given location.
 *
 * @param tableData - Table data to update
 * @param location - Location describing where the annotation should be applied
 * @param annotation - Optional annotation to apply (undefined removes the annotation)
 * @returns Updated table data
 */
export const updateTableDataWithAnnotations = <T>(
  tableData: AitTableData<T>,
  location: AitLocation,
  annotation?: AnnotationWhereGroup,
): AitTableData<T> => {
  switch (location.position) {
    case AitTablePosition.table: {
      // Update the global annotation for the entire table
      return {
        ...tableData,
        tableAnnotation: annotation,
      };
    }
    case AitTablePosition.header: {
      // Update the column annotation in the headerData
      if (tableData.headerData) {
        return {
          ...tableData,
          headerData: {
            ...tableData.headerData,
            annotation: annotation,
          },
        };
      }
      return tableData;
    }
    case AitTablePosition.headerRow: {
      // Update the annotation for a specific header row
      if (tableData.headerData) {
        return {
          ...tableData,
          headerData: {
            ...tableData.headerData,
            rows: tableData.headerData.rows.map((row) =>
              row.aitid === location.rowId
                ? {
                    ...row,
                    annotation: annotation,
                  }
                : row,
            ),
          },
        };
      }
      return tableData;
    }
    case AitTablePosition.column: {
      if (location.columnId) {
        const columnId = location.columnId;
        const originalColumnIndex = location.originalColumn;

        // Handle annotation removal
        if (!annotation) {
          const updatedColumnInformation = tableData.columnInformation.map((ca) =>
            ca.columnId === columnId ? { ...ca, annotation: undefined } : ca,
          );
          return {
            ...tableData,
            columnInformation: updatedColumnInformation,
          };
        }

        // Handle annotation addition/update
        const currentIdx = tableData.columnInformation.findIndex((ca) => ca.columnId === columnId);
        const newColumnInfo = { columnId, originalColumnIndex, annotation };

        // istanbul ignore next - Defensive fallback: columnInformation ?? [] is unreachable since line 77 would have thrown if undefined
        const updatedColumnInformation =
          currentIdx >= 0
            ? tableData.columnInformation!.map((ca, idx) =>
                idx === currentIdx ? newColumnInfo : ca,
              )
            : [...(tableData.columnInformation ?? []), newColumnInfo];

        return {
          ...tableData,
          columnInformation: updatedColumnInformation,
        };
      }
      return tableData;
    }
    case AitTablePosition.bodyRowGroup: {
      // Update the annotation for a specific body row group
      return {
        ...tableData,
        bodyData: tableData.bodyData.map((rowGroup) =>
          rowGroup.aitid === location.rowGroupId
            ? {
                ...rowGroup,
                annotation: annotation,
              }
            : rowGroup,
        ),
      };
    }
    case AitTablePosition.bodyRow: {
      // Update the annotation for a specific body row
      return {
        ...tableData,
        bodyData: tableData.bodyData.map((rowGroup) =>
          rowGroup.aitid === location.rowGroupId
            ? {
                ...rowGroup,
                rows: rowGroup.rows.map((row) =>
                  row.aitid === location.rowId
                    ? {
                        ...row,
                        annotation: annotation,
                      }
                    : row,
                ),
              }
            : rowGroup,
        ),
      };
    }
    default:
      return tableData;
  }
};
